import { parsePhoneNumberFromString } from 'libphonenumber-js';

// NANP default — works for both US and Canada (+1). National-format input is
// interpreted as US/CA; explicit `+`/`00` prefixes still parse to their country.
const DEFAULT_COUNTRY = 'US' as const;

export function normalizeContactPhoneToE164(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  // libphonenumber's US default IDD is 011, so map a leading 00 to + ourselves.
  const input = trimmed.startsWith('00') ? `+${trimmed.slice(2)}` : trimmed;
  const parsed = parsePhoneNumberFromString(input, DEFAULT_COUNTRY);
  return parsed?.isPossible() ? parsed.number : null;
}

export function formatPhoneDisplay(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = parsePhoneNumberFromString(value, DEFAULT_COUNTRY);
  if (!parsed) return value; // unparseable (e.g. test data) → return raw
  const national = parsed.formatNational();
  /*
   * Group a NANP number ourselves when the library will not — and ONLY then.
   *
   * `formatNational()` only formats numbers it considers VALID, and validity
   * is stricter than the shape: an area code starting 0 or 1 is impossible in
   * the NANP, so a stored "+11064231854" came back as bare digits — and this
   * function then glued a country code to them and printed "+1 1064231854"
   * beside properly formatted neighbours. The digits are the customer's; only
   * the separators are ours to add, so such a number is grouped whether or not
   * the exchange really exists.
   *
   * Gating this on the SHAPE alone (any ten-digit +1 number) sent every valid
   * number down the hand-built path too, and the hand-built path knows about
   * ten digits and nothing else — so "+1 (604) 555-0100 ext. 123" lost its
   * extension on both contact surfaces. The library's own output carries the
   * extension; what identifies the case this exists for is the library
   * declining, which it signals by handing the national number back unchanged.
   */
  const libraryDeclined = national.startsWith(parsed.nationalNumber);
  if (
    libraryDeclined &&
    parsed.countryCallingCode === '1' &&
    /^\d{10}$/.test(parsed.nationalNumber)
  ) {
    const digits = parsed.nationalNumber;
    // `formatNational()` puts the extension after the number; grouping the
    // digits ourselves means carrying it across ourselves too.
    const ext = parsed.ext ? ` ext. ${parsed.ext}` : '';
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}${ext}`;
  }
  // +1 (xxx) xxx-xxxx — country calling code + national grouping (read-only
  // views). Already carries the extension where there is one.
  return `+${parsed.countryCallingCode} ${national}`;
}

/**
 * Lenient validity for form input. Uses `isPossible()` (right length for the
 * NANP / parseable international) rather than strict `isValid()`, so fictional
 * and test numbers (e.g. 555-xxxx) and the existing seed/test data are accepted
 * and stay in sync with `normalizeContactPhoneToE164`.
 */
export function isValidContactPhone(value: string | null | undefined): boolean {
  return normalizeContactPhoneToE164(value) !== null;
}

/**
 * The key two spellings of one phone number share: its last ten digits, or ''
 * when there are fewer than ten. It is the same key the server stores in
 * `contacts.phoneSearchKeys`, so a client-side "is this the same number?" check
 * agrees with the search that produced the candidates.
 *
 * Comparing every digit instead is what these checks used to do, and it fails
 * on the commonest pair there is: the phone input emits `+15125073477` (eleven
 * digits) while QuickBooks-synced contacts store `(512) 507-3477` (ten).
 */
export function phoneMatchKey(value: string | null | undefined): string {
  // Drop a trailing extension (`x123`, `ext. 45`) first: its digits would shift
  // the key off the number, exactly as the server's derivation avoids.
  const digits = (value ?? '')
    .replace(/\s*(?:ext\.?|extension|x|#)\s*[.:]?\s*\d+\s*$/i, '')
    .replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : '';
}

type ContactPhones = {
  primaryPhone?: string | null;
  altPhone?: string | null;
  phoneNumbers?: Array<{ number?: string | null }> | null;
};

/**
 * Whether any of the contact's numbers is `phone`. Every number, not just the
 * primary: the server finds a contact by their mobile or a secondary line, and
 * a check that only reads `primaryPhone` then throws that match away.
 */
export function contactHasPhone(
  contact: ContactPhones | null | undefined,
  phone: string | null | undefined,
): boolean {
  const key = phoneMatchKey(phone);
  if (!key || !contact) return false;
  return [
    contact.primaryPhone,
    contact.altPhone,
    ...(contact.phoneNumbers ?? []).map((entry) => entry?.number),
  ].some((number) => phoneMatchKey(number) === key);
}
